import { useEffect, useMemo, useState } from 'react';

import { DeviceInfo } from '@ezugi/utils';

const subscribeToOrientationChange = (cb: () => void) => {
  if (typeof screen === 'object' && screen.orientation) {
    const existingOnChange = screen.orientation.onchange;
    screen.orientation.onchange = (e) => {
      // @ts-ignore
      typeof existingOnChange === 'function' && existingOnChange(e);
      cb();
    };
    return () => { screen.orientation.onchange = existingOnChange; };
  }
  window.addEventListener('orientationchange', cb);
  return () => window.removeEventListener('orientationchange', cb);
};

const isWindow = (target: Window | HTMLDivElement): target is Window => target === window;

const getTargetSize = (target: Window | HTMLDivElement | null) => {
  if (target === null) {
    return {
      width: 0,
      height: 0,
    };
  }

  const _isWindow = isWindow(target);

  return {
    width: _isWindow ? window.innerWidth : target.offsetWidth,
    height: _isWindow ? window.innerHeight : target.offsetHeight,
  };
};

function useRootSize() {
  const target = useMemo(() => (DeviceInfo.isHandheld ? window : document.getElementById('app')) as HTMLDivElement, []);
  const [ size, setSize ] = useState(getTargetSize(target));
  const onResize = () => setSize(getTargetSize(target));
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      onResize();
    }
  };
  useEffect(() => {
    window.addEventListener('resize', onResize);
    let unsubscribe = () => {};
    if (DeviceInfo.isIOS) {
      unsubscribe = subscribeToOrientationChange(onResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', onResize);
      }
    }

    const intervalId = setInterval(() => {
      setSize((prevSize) => {
        const newSize = getTargetSize(target);
        return newSize.width !== prevSize.width || newSize.height !== prevSize.height
          ? newSize
          : prevSize;
      });
    }, 300);

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('resize', onResize);
      unsubscribe();
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ target ]);

  return size;
}

export { useRootSize };
